import { TFetchConverter } from 'data/types/Converter.types';
import {
  EAnimalHistoryActionDB,
  EChangeSourceDB,
  TAnimalHistoryItemDB,
  TAnimalHistoryItemUI,
} from 'data/types/Animal.types';
import { dateTimeFormat } from 'helpers/utils';
import { formattedDateTimezone } from './General.converters';

const animalHistoryActionLabels: {
  [key in EAnimalHistoryActionDB]: string;
} = {
  [EAnimalHistoryActionDB.ANIMAL_CREATED]: 'Animal created',
  [EAnimalHistoryActionDB.ANIMAL_DELETED]: 'Animal deleted',
  [EAnimalHistoryActionDB.ANIMAL_COMPLETED]: 'Animal profile completed',
  [EAnimalHistoryActionDB.ANIMAL_NAME_UPDATED]: 'Name updated',
  [EAnimalHistoryActionDB.ANIMAL_WEIGHT_UPDATED]: 'Weight updated',
  [EAnimalHistoryActionDB.ANIMAL_SPECIE_UPDATED]: 'Species updated',
  [EAnimalHistoryActionDB.ANIMAL_BREED_UPDATED]: 'Breed updated',
  [EAnimalHistoryActionDB.ANIMAL_NEW_PHOTO_ADDED]: 'Photo added',
  [EAnimalHistoryActionDB.ANIMAL_PHOTO_REMOVED]: 'Photo removed',
  [EAnimalHistoryActionDB.ANIMAL_BIRTHDAY_UPDATED]: 'Birthday updated',
  [EAnimalHistoryActionDB.ANIMAL_GENDER_UPDATED]: 'Gender updated',
  [EAnimalHistoryActionDB.ANIMAL_REPRODUCTIVE_UPDATED]:
    'Spayed/neutered status updated',
  [EAnimalHistoryActionDB.ANIMAL_HOUSE_TRAINED_UPDATED]:
    'House trained updated',
  [EAnimalHistoryActionDB.ANIMAL_PRIMARY_COLOR_UPDATED]:
    'Primary color updated',
  [EAnimalHistoryActionDB.ANIMAL_NEW_VET_RECORD_ADDED]: 'Vet record added',
  [EAnimalHistoryActionDB.ANIMAL_VET_RECORD_UPDATED]: 'Vet record updated',
  [EAnimalHistoryActionDB.ANIMAL_VET_RECORD_REMOVED]: 'Vet record removed',
  [EAnimalHistoryActionDB.ANIMAL_NEW_HP_DOCUMENT_ADDED]: 'HP document added',
  [EAnimalHistoryActionDB.ANIMAL_HP_DOCUMENT_UPDATED]: 'HP document updated',
  [EAnimalHistoryActionDB.ANIMAL_HP_DOCUMENT_REMOVED]: 'HP document removed',
  [EAnimalHistoryActionDB.ANIMAL_NEW_SA_DOCUMENT_ADDED]: 'SA document added',
  [EAnimalHistoryActionDB.ANIMAL_SA_DOCUMENT_REMOVED]: 'SA document removed',
  [EAnimalHistoryActionDB.ANIMAL_SA_DOCUMENT_UPDATED]: 'SA document updated',
  [EAnimalHistoryActionDB.ANIMAL_CONVERSION_CREATED]: 'Conversion requested',
  [EAnimalHistoryActionDB.ANIMAL_CONVERTED]: 'Converted',
  [EAnimalHistoryActionDB.ANIMAL_HARD_CONVERTED]: 'Hard converted',
  [EAnimalHistoryActionDB.ANIMAL_CONVERSION_APPROVED]: 'Conversion approved',
  [EAnimalHistoryActionDB.ANIMAL_VACCINATION_RECORD_EXPIRATION_UPDATED]:
    'Vaccination expiration updated',
  [EAnimalHistoryActionDB.ANIMAL_VACCINATION_RECORD_REMOVED]:
    'Vaccination record removed',
  [EAnimalHistoryActionDB.ANIMAL_VACCINATION_RECORD_TYPE_CHANGED]:
    'Vaccination record type changed',
  [EAnimalHistoryActionDB.ANIMAL_VET_RECORD_DOCUMENT_FILE_ATTACHED]:
    'Vet record file attached',
  [EAnimalHistoryActionDB.ANIMAL_VACCINATION_RECORD_ATTACHED]:
    'Vaccination record attached',
  [EAnimalHistoryActionDB.ANIMAL_VET_RECORD_DOCUMENT_FILE_REMOVE]:
    'Vet record file removed',
  [EAnimalHistoryActionDB.ANIMAL_OWNER_CHANGED]: 'Owner changed',
  [EAnimalHistoryActionDB.ANIMAL_AGGRESSIVE_BEHAVIOR_UPDATED]:
    'Aggressive behavior updated',
  [EAnimalHistoryActionDB.ANIMAL_TYPE_CONFIRMED]: 'Type confirmed',
  [EAnimalHistoryActionDB.ANIMAL_TYPE_CHANGED]: 'Type changed',
  [EAnimalHistoryActionDB.ANIMAL_MICROCHIP_NUMBER_UPDATE]:
    'Microchip number updated',
  [EAnimalHistoryActionDB.ANIMAL_VERIFIED]: 'Verified',
  [EAnimalHistoryActionDB.ANIMAL_VERIFICATION_RETRACTED]:
    'Verification retracted',
  [EAnimalHistoryActionDB.ANIMAL_VACCINATION_RECORD_VERIFIED]:
    'Vaccination record verified',
  [EAnimalHistoryActionDB.ANIMAL_VACCINATION_RECORD_VERIFICATION_REMOVED]:
    'Vaccination record verification removed',
  [EAnimalHistoryActionDB.ANIMAL_HP_DOCUMENT_VERIFIED]: 'HP document verified',
  [EAnimalHistoryActionDB.ANIMAL_HP_DOCUMENT_VERIFICATION_REMOVED]:
    'HP document verification removed',
  [EAnimalHistoryActionDB.ANIMAL_APPLICATION_CREATED]: 'Application created',
  [EAnimalHistoryActionDB.ANIMAL_APPLICATION_STATUS_RESETTED]:
    'Application status reset',
  [EAnimalHistoryActionDB.ANIMAL_APPLICATION_REJECTED]:
    'Application not approved',
  [EAnimalHistoryActionDB.ANIMAL_APPLICATION_ARCHIVED]: 'Application archived',
  [EAnimalHistoryActionDB.ANIMAL_APPLICATION_APPROVED]: 'Application approved',
  [EAnimalHistoryActionDB.CONDITIONALLY_APPROVED_APPLICATION_APPROVED]:
    'Conditional approval approved',
  [EAnimalHistoryActionDB.ANIMAL_APPLICATION_RETRACTED]:
    'Application retracted',
  [EAnimalHistoryActionDB.ANIMAL_APPLICATION_ABANDONED]:
    'Application abandoned',
};

/**
 * The backend names the system itself explicitly, so a null actor here means it genuinely could
 * not say who acted - a deleted user, or one with no name. Labelling that "OPP system" would
 * attribute an offboarded admin's retraction to an automated process, which is the misattribution
 * this feature exists to prevent.
 */
const UNKNOWN_ACTOR_NAME = 'Unknown';

const changeSourceLabels: { [key in EChangeSourceDB]: string } = {
  [EChangeSourceDB.SYSTEM]: 'OPP',
  [EChangeSourceDB.RESMAN]: 'ResMan',
  [EChangeSourceDB.YARDI]: 'Yardi',
  [EChangeSourceDB.REAL_PAGE]: 'RealPage',
  [EChangeSourceDB.APPFOLIO]: 'AppFolio',
  [EChangeSourceDB.HUB_SPOT]: 'HubSpot',
  [EChangeSourceDB.FUNNEL]: 'Funnel',
  [EChangeSourceDB.ENTRATA]: 'Entrata',
  [EChangeSourceDB.SYNC_INTEGRATION]: 'Sync integration',
  [EChangeSourceDB.PARTNER_API_INTEGRATION]: 'Partner API',
  [EChangeSourceDB.SCIM]: 'SCIM',
};

/**
 * Timestamps arrive as UTC, so they are rendered through the timezone-aware formatter: the whole
 * point of this list is answering "when did this change", and an entry shown hours off would
 * answer it wrongly.
 *
 * An action type or source the backend added but this app does not know yet falls back to the raw
 * constant rather than rendering blank — an unfamiliar label is still a usable audit entry, an
 * empty cell is not.
 */
export const animalHistoryItemConverter: TFetchConverter<
  TAnimalHistoryItemUI,
  TAnimalHistoryItemDB
> = {
  fromDb: data => ({
    action: animalHistoryActionLabels[data.actionType] || data.actionType,
    occurredOn: formattedDateTimezone(data.occurredOn, dateTimeFormat),
    actorName: data.actorName || UNKNOWN_ACTOR_NAME,
    source: changeSourceLabels[data.source] || data.source,
    description: data.description || undefined,
  }),
};
